import type { User } from 'oidc-client-ts';
import authService from '../../services/authService';
import { permissionManager } from './PermissionManager';
import type { Routemize_DTOs_AppConfiguration_AppConfigurationDto } from '../../api/models/Routemize_DTOs_AppConfiguration_AppConfigurationDto';
import { SYSTEM_ROLE_NAMES } from '../users/UserRoleConfig';
import { config } from '../../config/config';
import { PartnerPortalService } from '../../api/services/PartnerPortalService';
import { Routemize_Constants_PartnerUserRole as PartnerUserRole } from '../../api/models/Routemize_Constants_PartnerUserRole';

export interface AdminPermissions {
  canManageTenants: boolean;
  canManageUsers: boolean;
  canManageUserRoles: boolean;
  canManageUserPermissions: boolean;
  canManageRoles: boolean;
  canManageRolePermissions: boolean;
  canManageTenantFeatures: boolean;
  canManageEmailSettings: boolean;
  canManageTimezoneSettings: boolean;
  canManageMasterData: boolean;
  canManageWebhooks: boolean;
  canManageMessageTemplates: boolean;
  canManageApiKeys: boolean;
  canManageEmailActivity: boolean;
  /** Host-only: email selected tenants' account owners from Admin Analytics (Routemize.TenantOutreach.Send). */
  canSendTenantOutreach: boolean;
}

export interface AdminUserInfo {
  isHostAdmin: boolean;
  isTenantAdmin: boolean;
  isPartner: boolean;
  /**
   * True when the logged-in partner user is an Owner of their company (partners only).
   * Owner/Member is NOT an ABP permission — every partner user shares the one `Partner`
   * role and holds the full `Partner.*` policy set — so this is derived from the caller's
   * own RM_PartnerUsers.Role. Owner-only UI (Billing, team management) gates on this.
   * Defaults to false (Member) if the role can't be resolved, so billing stays hidden.
   */
  isPartnerOwner: boolean;
  tenantId?: string;
  tenantName?: string;
  /** The logged-in partner's company name (partners only). */
  partnerCompanyName?: string;
  /** The logged-in partner's contact name (partners only). */
  partnerName?: string;
  permissions: AdminPermissions;
  roles: string[];
}

export interface AdminNavItem {
  path: string;
  label: string;
  icon: string;
  requiredPermission: keyof AdminPermissions | null;
}

/**
 * AdminUserManager - Business logic for admin user management and authorization
 * Handles admin role validation, permission calculation, and navigation generation
 */
class AdminUserManager {
  /**
   * Get current user's admin information using role and ABP policies
   */
  async getAdminUserInfo(appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<AdminUserInfo | null> {
    const user = await authService.getUser();
    if (!user) return null;

    // Get roles from ABP Application Configuration if available, otherwise fallback to OIDC profile
    const roles = appConfig?.currentUser?.roles || this.extractRoles(user);
    
    // Business rule: Must have admin role
    if (!this.isAdmin(roles)) {
      return null;
    }

    // Business rule: Must have admin access through ABP policies
    if (!permissionManager.canAccessAdminArea(appConfig)) {
      return null;
    }

    // Determine admin type based on tenant context and role
    const hasTenantContext = !!appConfig?.currentUser?.tenantId;
    // Set on the token by ImpersonationExtensionGrant (AbpClaimTypes.ImpersonatorUserId)
    // whenever a host admin is viewing the app as another user.
    const isImpersonating = !!appConfig?.currentUser?.impersonatorUserId;

    const isPartnerRole = this.isPartner(roles);

    // Business rule: the tenant-context admin area is a Routemize-staff tool. A tenant
    // super-admin may only reach it while a host admin is impersonating them — a tenant
    // admin signed in on their own credentials gets no admin access. Host admins (no
    // tenant context) are unaffected and keep their own host-mode admin access.
    if (hasTenantContext && !isImpersonating) {
      return null;
    }

    // Partners are host-level users (no tenantId) but must NOT inherit staff host-admin pages.
    const isHostAdmin = !hasTenantContext && !isPartnerRole;
    const isTenantAdmin = hasTenantContext;
    const isPartner = !hasTenantContext && isPartnerRole;

    // Partners have no tenant context, so their company/name isn't in appConfig.
    // Fetch the partner record to label the portal. Non-fatal on failure.
    let partnerCompanyName: string | undefined;
    let partnerName: string | undefined;
    let isPartnerOwner = false;
    if (isPartner) {
      try {
        const partner = await PartnerPortalService.getApiAppPartnerPortalGetMyPartner();
        partnerCompanyName = partner.companyName ?? undefined;
        partnerName = partner.name ?? undefined;
      } catch {
        // Fall back to the generic "Partner" label if the record can't load.
      }

      // Derive the caller's Owner/Member role from their own membership row.
      // Backend authoritatively enforces owner-only operations; this only drives UI
      // visibility. Case-insensitive GUID match; defaults to Member on any failure.
      try {
        const currentUserId = appConfig?.currentUser?.id?.toLowerCase();
        if (currentUserId) {
          const members = await PartnerPortalService.getApiAppPartnerPortalGetMyPartnerUsers();
          const me = members.find(m => (m.identityUserId ?? '').toLowerCase() === currentUserId);
          isPartnerOwner = me?.role === PartnerUserRole.OWNER;
        }
      } catch {
        // Stay Member (billing hidden) if membership can't be resolved.
      }
    }

    return {
      isHostAdmin,
      isTenantAdmin,
      isPartner,
      isPartnerOwner,
      tenantId: appConfig?.currentUser?.tenantId,
      tenantName: appConfig?.currentTenant?.name,
      partnerCompanyName,
      partnerName,
      permissions: this.calculatePermissions(appConfig),
      roles
    };
  }

  /**
   * Check if user has host admin privileges
   */
  async isHostAdminAsync(appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<boolean> {
    const adminInfo = await this.getAdminUserInfo(appConfig);
    return adminInfo?.isHostAdmin ?? false;
  }

  /**
   * Check if user has tenant admin privileges
   */
  async isTenantAdminAsync(appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<boolean> {
    const adminInfo = await this.getAdminUserInfo(appConfig);
    return adminInfo?.isTenantAdmin ?? false;
  }

  /**
   * Check if user has any admin privileges
   */
  async isAnyAdmin(appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<boolean> {
    return permissionManager.canAccessAdminArea(appConfig);
  }

  /**
   * Check if user has specific admin permission
   */
  async hasPermission(permission: keyof AdminPermissions, appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<boolean> {
    const adminInfo = await this.getAdminUserInfo(appConfig);
    return adminInfo?.permissions[permission] ?? false;
  }

  /**
   * Get admin navigation items based on user permissions
   */
  async getAdminNavItems(appConfig?: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Promise<AdminNavItem[]> {
    const adminInfo = await this.getAdminUserInfo(appConfig);
    if (!adminInfo) return [];

    // Partners only see their own pages
    if (adminInfo.isPartner) {
      const partnerItems: AdminNavItem[] = [
        { path: '/admin/partner-portal', label: 'My Clients', icon: 'Handshake', requiredPermission: null },
        { path: '/admin/partner/access-hub', label: 'Access Hub', icon: 'KeyRound', requiredPermission: null },
        { path: '/admin/partner/analytics', label: 'Analytics', icon: 'BarChart3', requiredPermission: null },
      ];
      // Billing is Owner-only (backend gates it via EnsureCallerIsOwnerAsync).
      if (adminInfo.isPartnerOwner) {
        partnerItems.push({ path: '/admin/partner/billing', label: 'Billing', icon: 'CreditCard', requiredPermission: null });
      }
      // Team is visible to all members (Members get a read-only list; Owners get controls).
      partnerItems.push(
        { path: '/admin/partner/team', label: 'Team', icon: 'Users', requiredPermission: null },
        { path: '/admin/partner/resources', label: 'Resources', icon: 'GraduationCap', requiredPermission: null },
      );
      return partnerItems;
    }

    const items: AdminNavItem[] = [];

    // Dashboard entry — host admins see platform analytics, tenant admins see debug sessions.
    items.push({
      path: '/admin/dashboard',
      label: adminInfo.isHostAdmin ? 'Analytics' : 'Schedule Debug',
      icon: adminInfo.isHostAdmin ? 'BarChart3' : 'Clock',
      requiredPermission: null
    });

    // Host Admin specific items
    if (adminInfo.isHostAdmin && adminInfo.permissions.canManageTenants) {
      items.push({
        path: '/admin/tenants',
        label: 'Tenants',
        icon: 'Building',
        requiredPermission: 'canManageTenants'
      });
    }

    // Leads — users who signed up but didn't complete registration.
    if (adminInfo.isHostAdmin && adminInfo.permissions.canManageTenants) {
      items.push({
        path: '/admin/leads',
        label: 'Leads',
        icon: 'UserPlus',
        requiredPermission: 'canManageTenants'
      });
    }

    // Partner Hub — host-admin surface to provision/manage partner logins.
    // Distinct from the Partner Portal (/admin/partner-portal), which is the
    // partner's own self-service area gated by requirePartner.
    if (adminInfo.isHostAdmin && adminInfo.permissions.canManageTenants) {
      items.push({
        path: '/admin/partner-hub',
        label: 'Partner Hub',
        icon: 'Handshake',
        requiredPermission: 'canManageTenants'
      });
    }

    // Account Links — staff surface for joining the accounts one person owns, which is what
    // makes the customer-facing account switcher appear. The only way to create links today.
    if (adminInfo.isHostAdmin && adminInfo.permissions.canManageTenants) {
      items.push({
        path: '/admin/account-links',
        label: 'Account Links',
        icon: 'Link2',
        requiredPermission: 'canManageTenants'
      });
    }

    // System Announcements — host-admin-only platform-wide broadcast tool.
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/announcements',
        label: 'Announcements',
        icon: 'Megaphone',
        requiredPermission: null
      });
    }

    // Tenant Emails — history of host-admin outreach sends to tenant account owners.
    if (adminInfo.isHostAdmin && adminInfo.permissions.canSendTenantOutreach) {
      items.push({
        path: '/admin/tenant-outreach',
        label: 'Tenant Emails',
        icon: 'Send',
        requiredPermission: 'canSendTenantOutreach'
      });
    }


    // OAuth Applications management (Host Admin only)
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/applications',
        label: 'Applications',
        icon: 'FileText',
        requiredPermission: null
      });
    }


    // Master Data management (Both Host and Tenant Admin)
    if (adminInfo.permissions.canManageMasterData) {
      items.push({
        path: '/admin/master-data',
        label: 'Master Data',
        icon: 'Database',
        requiredPermission: 'canManageMasterData'
      });
    }

    // Message Templates
    items.push({
      path: '/admin/templates',
      label: 'Templates',
      icon: 'Mail'
    });

    // Both Host and Tenant Admin items
    if (adminInfo.permissions.canManageUsers) {
      items.push({
        path: '/admin/users',
        label: 'Users',
        icon: 'Users',
        requiredPermission: 'canManageUsers'
      });
    }

    // Tenant-specific feature management
    if (adminInfo.isTenantAdmin && adminInfo.permissions.canManageTenantFeatures) {
      items.push({
        path: '/admin/tenant-features',
        label: 'Features',
        icon: 'Zap',
        requiredPermission: 'canManageTenantFeatures'
      });
    }

    // Identity & Access Management (Both Host and Tenant Admin)
    items.push({
      path: '/admin/iam',
      label: 'Identity & Access',
      icon: 'Key',
      requiredPermission: null
    });

    // API Keys (Tenant Admin only)
    if (!adminInfo.isHostAdmin && adminInfo.permissions.canManageApiKeys) {
      items.push({
        path: '/admin/api-keys',
        label: 'API Keys',
        icon: 'KeyRound',
        requiredPermission: 'canManageApiKeys'
      });
    }

    // Email Activity (Both Host and Tenant Admin)
    if (adminInfo.permissions.canManageEmailActivity) {
      items.push({
        path: '/admin/email-activity',
        label: 'Email Activity',
        icon: 'MailOpen',
        requiredPermission: 'canManageEmailActivity'
      });
    }

    // Tutorial Videos (Host Admin only)
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/tutorials',
        label: 'Tutorial Videos',
        icon: 'Video',
        requiredPermission: null
      });
    }

    // Voice Agent Calls — cross-tenant Vapi call log (Host Admin only).
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/voice-agent',
        label: 'Voice Agent Calls',
        icon: 'Mic',
        requiredPermission: null
      });
    }

    // Routy Usage — AI assistant adoption/volume (Host Admin only).
    // Gated behind config.routy.enabled so it stays hidden while Routy is a
    // browser-Anthropic prototype (off in prod until the backend agent ships).
    if (adminInfo.isHostAdmin && config.routy.enabled) {
      items.push({
        path: '/admin/routy-usage',
        label: 'Routy Usage',
        icon: 'Sparkles',
        requiredPermission: null
      });
    }

    // Chargebee Webhooks (Host Admin only)
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/chargebee-webhooks',
        label: 'Chargebee Webhooks',
        icon: 'Webhook',
        requiredPermission: null
      });
    }

    // Public Submission Blocks — bot-defense audit log (Host Admin only)
    if (adminInfo.isHostAdmin) {
      items.push({
        path: '/admin/security/public-submission-blocks',
        label: 'Submission Blocks',
        icon: 'Shield',
        requiredPermission: null
      });
    }

    return items;
  }

  // Private Business Logic Methods

  /**
   * Extract user roles from OIDC claims
   */
  private extractRoles(user: User): string[] {
    const roles = user.profile?.role || user.profile?.roles || [];
    return Array.isArray(roles) ? roles : [roles];
  }

  /**
   * Check if user has admin role
   * Handles both new system role names and legacy 'admin' role for backwards compatibility
   * Admin roles: 'Super User' (new), 'admin' (legacy), 'Partner' (partner portal)
   */
  private isAdmin(roles: string[]): boolean {
    const normalizedRoles = roles.map(role => role.toLowerCase().trim());

    // Check for new system role: "Super User"
    const superUserName = SYSTEM_ROLE_NAMES[0].toLowerCase(); // 'super user'
    if (normalizedRoles.some(role => role === superUserName)) {
      return true;
    }

    // Check for legacy 'admin' role for backwards compatibility
    if (normalizedRoles.some(role => role === 'admin')) {
      return true;
    }

    // Check for partner role
    if (normalizedRoles.some(role => role === 'partner')) {
      return true;
    }

    return false;
  }

  private isPartner(roles: string[]): boolean {
    return roles.map(r => r.toLowerCase().trim()).includes('partner');
  }

  /**
   * Calculate permissions based on ABP policies
   */
  private calculatePermissions(appConfig: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): AdminPermissions {
    return {
      canManageTenants: permissionManager.canAccessTenantManagement(appConfig),
      canManageUsers: permissionManager.canAccessUserManagement(appConfig),
      canManageUserRoles: permissionManager.canManageUserRoles(appConfig),
      canManageUserPermissions: permissionManager.canManageUserPermissions(appConfig),
      canManageRoles: permissionManager.canAccessRoleManagement(appConfig),
      canManageRolePermissions: permissionManager.canManageRolePermissions(appConfig),
      canManageTenantFeatures: permissionManager.canManageTenantFeatures(appConfig),
      canManageEmailSettings: permissionManager.canManageEmailSettings(appConfig),
      canManageTimezoneSettings: permissionManager.canManageTimezoneSettings(appConfig),
      canManageMasterData: permissionManager.canManageMasterData(appConfig),
      canManageWebhooks: permissionManager.hasPermission(appConfig, 'Routemize.Webhooks.Read'),
      canManageMessageTemplates: permissionManager.hasPermission(appConfig, 'Routemize.Admin'),
      canManageApiKeys: permissionManager.hasPermission(appConfig, 'Routemize.ApiKeys'),
      canManageEmailActivity: permissionManager.hasPermission(appConfig, 'Routemize.EmailActivity') || permissionManager.hasPermission(appConfig, 'Routemize.EmailActivity.ViewOwn'),
      canSendTenantOutreach: permissionManager.hasPermission(appConfig, 'Routemize.TenantOutreach.Send'),
    };
  }
}

export default new AdminUserManager();